import RequiredActionsModal from 'components/RequiredActionsModal/RequiredActionsModal';
import axios from 'axios';
import companyNames from 'constants/companyNames';
import propTypes from 'prop-types';
import { arrayDifference } from 'helpers/arrays';
import { scrollElementToContainerTop } from 'helpers/dom/scroll';
import { useDispatch, useSelector } from 'react-redux';
import { setRequestedCarrierName } from 'state/actions/appointments';

import {
  getCarrierRequiredActions,
  isBlackoutPeriod,
  KAISER_GA_REQUIRED_FIELDS,
  KAISER_GA_TRANSIENT_FIELDS
} from 'helpers/requiredActions/requiredActions';

import {
  getSuncareCardStatus,
  getSuncareTrainingBadgeClassName,
  getSuncareUiState,
  SUNCARE_PHASES,
  SUNCARE_REQUIRED_FIELDS,
  SUNCARE_TRANSIENT_FIELDS
} from 'helpers/suncare/suncare';
import {
  shouldUseSuncareSendContract,
  sendSuncareContractSent
} from 'state/actions/suncare';
import SuncareInProgressNotice from 'components/SuncareInProgressNotice/SuncareInProgressNotice';
import SuncareTrainingNotice from 'components/SuncareTrainingNotice/SuncareTrainingNotice';
import { TRANSFER_TYPES, STATE_LIST_CERT } from 'constants/enum';
import { cn } from 'lib/utils';

import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  useMemo
} from 'react';
import {
  getBcbsCrossRequestBlockMessage,
  getCarrierStateOptions,
  getCarrierDisabledDueToBlackoutData,
  isCarrierBlockedByBcbsCrossRequest
} from 'helpers/appointments/appointments';
import { shouldBlockForFfmCertification } from 'helpers/appointments/ffmCertification';
import { isAgentRankLimited } from 'helpers/agents';
import { isNeoCarrier } from 'helpers/neo/neo';

import { getCompany } from 'helpers/getCompanyVariables';
import { DISCLAIMER_PLACEMENTS } from 'helpers/factory/carrierFactory';
import CarrierDisclaimer from 'tailwind-components/CarrierDisclaimer/CarrierDisclaimer';
import { AGENCY_DISABLED_CARRIER_MESSAGE } from 'helpers/carriers/carriers';
import TransferTypeSelector from './TransferTypeSelector';
import StateSelector from './StateSelector';
import { AppointmentMessages } from './AppointmentMessages';
import CarrierFeatureNotes from './CarrierFeatureNotes';
import NeoContractingSection from './NeoContractingSection';
import ScrollToRequestButton from './ScrollToRequestButton';
import SendRequestButton from './SendRequestButton';
import SBEModal from './SBEModal';
import useIsElementInView from './useIsElementInView';

const company = getCompany();

const isOneill = company === 'oneill';

const INITIAL_VALUES = {
  appointments: [],
  appointedType: TRANSFER_TYPES.NewAppointment,
  files: [],
  expirationDates: [],
  acknowledgement: false,
  providenceAcknowledgement: false,
  bcbsNcAcknowledgement: false,
  bcbsScAcknowledgement: false,
  ...KAISER_GA_REQUIRED_FIELDS,
  ...SUNCARE_REQUIRED_FIELDS
};

const SideSectionUnnapointed = forwardRef(function Component(
  {
    confirmed = [],
    carrier,
    statesOffered,
    carrierConsidersStates,
    carrierData,
    createAppointmentRequest,
    obFormCompleted,
    simplified,
    setApptRequestStatus,
    carrierInProgress,
    isAgencyDisabled
  },
  ref
) {
  const cancelTokenSource = axios.CancelToken.source();
  // Redux
  const { isSendingRequestAppointments, requestedAppointmentsByCarrier } =
    useSelector(state => state.appointments);
  const { attachments } = useSelector(state => state.appointments);
  const user = useSelector(state => state.user.zohoData);

  const isAgentLimited = isAgentRankLimited(user.Rank);

  const isNeo = isNeoCarrier(carrierData);

  // the scroll lands on the section top, but visibility is driven by the send request button,
  // otherwise a sliver of the section title peeking in would already hide the shortcut
  const requestSectionRef = useRef(null);
  const sendRequestButtonRef = useRef(null);

  const scrollToRequestSection = () => {
    scrollElementToContainerTop(requestSectionRef.current);
  };

  const isFfmCertificationBlocked = shouldBlockForFfmCertification(
    carrierData?.type,
    user?.FFM_Certification_Expiration
  );

  const isRankRestrictedCarrier =
    carrierData.availableAgentRanks.size > 0 &&
    !carrierData.availableAgentRanks.has(user.Rank);

  const lowRankCarrierMessage = isRankRestrictedCarrier
    ? `Unfortunately, due to the limited spots ${carrier} is giving agencies this year, we are not able to proceed with your request at this time.`
    : '';

  const dispatch = useDispatch();
  // Local
  const [formData, setFormData] = useState(INITIAL_VALUES);
  const [isOpenSBEModal, setIsOpenSBEModal] = useState(false);
  const [isOpenRequiredActionsModal, setIsOpenRequiredActionsModal] =
    useState(false);
  // required actions are computed on demand at submit
  const [requiredActions, setRequiredActions] = useState({
    filteredActions: []
  });

  const blackoutData = useMemo(
    () => ({
      transferBlackoutData: isBlackoutPeriod(
        carrierData.transferBlackout,
        formData.appointments
      ),
      contractingBlackoutData: isBlackoutPeriod(
        carrierData.contractingBlackout,
        formData.appointments
      )
    }),
    [carrierData, carrierData?.name, formData.appointments]
  );

  const { transferBlackoutData, contractingBlackoutData } = blackoutData;

  const { filteredActions } = requiredActions;

  const {
    isCarrier: isSuncareCarrier,
    isPendingDirector: isSuncarePendingDirector,
    isTrainingPending: isSuncareTrainingPending,
    isAppointmentBlocked: isSuncareAppointmentBlocked
  } = getSuncareUiState(user, carrier);
  const isSuncareContractStep = shouldUseSuncareSendContract(
    filteredActions,
    user
  );

  useEffect(() => {
    if (isSuncareCarrier) {
      setFormData(prevState => ({
        ...prevState,
        appointedType: TRANSFER_TYPES.NewAppointment
      }));
    }
  }, [carrier, isSuncareCarrier]);

  // derive blackout flags from the cheap isBlackoutPeriod memo, not from the full RA calc
  const {
    disabled: isCarrierDisabledDueToBlackout,
    message: carrierDisabledDueToBlackoutMessage
  } = getCarrierDisabledDueToBlackoutData(
    {
      isContractingBlackout: contractingBlackoutData.isBlackout,
      contractingBlackoutStates: contractingBlackoutData.states,
      isFullContractingBlackout:
        contractingBlackoutData.isBlackout &&
        contractingBlackoutData.states.length === 0,
      isTransferBlackout: transferBlackoutData.isBlackout,
      transferBlackoutStates: transferBlackoutData.states,
      isFullTransferBlackout:
        transferBlackoutData.isBlackout &&
        transferBlackoutData.states.length === 0
    },
    formData.appointments,
    formData.appointedType
  );

  const isBlockedByBcbsCrossRequest = isCarrierBlockedByBcbsCrossRequest(
    carrier,
    requestedAppointmentsByCarrier,
    formData.appointments
  );

  const bcbsCrossRequestBlockMessage = getBcbsCrossRequestBlockMessage(
    carrier,
    requestedAppointmentsByCarrier
  );

  // FFM states are already filtered out of `options`, so the remaining SBE states stay requestable.
  // carriers that don't break requests down by state have nothing to filter, so they stay blocked
  const isDisabled =
    !obFormCompleted ||
    (isFfmCertificationBlocked && !carrierConsidersStates) ||
    isAgencyDisabled ||
    (carrierConsidersStates && formData.appointments.length < 1) ||
    isSendingRequestAppointments ||
    isCarrierDisabledDueToBlackout ||
    isAgentLimited ||
    lowRankCarrierMessage ||
    isBlockedByBcbsCrossRequest;

  const handleAcknowledgement = (key, value) => {
    setFormData(prevState => ({
      ...prevState,
      [key]: value
    }));
  };

  useEffect(() => {
    setApptRequestStatus?.(_current => ({
      appointments: isSendingRequestAppointments
        ? _current.appointments
        : formData.appointments,
      disabled: isDisabled,
      loading: isSendingRequestAppointments
    }));
  }, [formData.appointments, isSendingRequestAppointments]);

  const handleChangeAppointments = values => {
    setFormData(prevState => ({
      ...prevState,
      appointments: values,
      files: [],
      expirationDates: [],
      acknowledgement: false,
      providenceAcknowledgement: false,
      bcbsNcAcknowledgement: false,
      bcbsScAcknowledgement: false,
      ...KAISER_GA_REQUIRED_FIELDS,
      ...SUNCARE_REQUIRED_FIELDS
    }));
  };

  const handleChangeAppointedType = e => {
    setFormData(prevState => ({ ...prevState, appointedType: e.target.value }));
  };

  const handleFilesChange = (index, files) => {
    setFormData(prevState => {
      const updatedFiles = [...prevState.files];
      updatedFiles[index] = files;
      return { ...prevState, files: updatedFiles };
    });
  };

  const handleExpirationDatesChange = (index, date) => {
    setFormData(prevState => {
      const updatedDates = [...prevState.expirationDates];
      updatedDates[index] = date;
      return { ...prevState, expirationDates: updatedDates };
    });
  };

  // compute required actions on demand for the exact states being submitted (subset-aware)
  const computeRequiredActions = appointments =>
    getCarrierRequiredActions(
      carrierData,
      appointments,
      attachments,
      formData.appointedType,
      isBlackoutPeriod(carrierData.transferBlackout, appointments),
      isBlackoutPeriod(carrierData.contractingBlackout, appointments),
      user
    );

  const handleSubmit = (appointments, overrideSBEModal = false) => {
    if (isSuncareAppointmentBlocked) return;
    // OB form already gates the button (disabled), so it is not re-checked here
    const areCertStatesIncluded = STATE_LIST_CERT.some(({ state }) =>
      appointments.includes(state)
    );

    if (areCertStatesIncluded && !overrideSBEModal) {
      setIsOpenSBEModal(true);
      return;
    }

    // RA computed once here, only for the exact states being submitted
    const nextRequiredActions = computeRequiredActions(appointments);
    setRequiredActions(nextRequiredActions);

    // narrow the request to the chosen states and size the RA arrays for them
    setFormData(prevState => ({
      ...prevState,
      appointments,
      files: nextRequiredActions.filteredActions.map(() => null),
      expirationDates: nextRequiredActions.filteredActions.map(() => null),
      acknowledgement: false,
      ...KAISER_GA_REQUIRED_FIELDS,
      ...SUNCARE_REQUIRED_FIELDS
    }));

    if (nextRequiredActions.filteredActions.length > 0) {
      setIsOpenSBEModal(false);
      setIsOpenRequiredActionsModal(true);
      return;
    }

    handleSendRequest(appointments, nextRequiredActions);
  };

  useImperativeHandle(
    ref,
    () => {
      return {
        handleRequestClick: () => handleSubmit(formData.appointments)
      };
    },
    [handleSubmit, formData.appointments]
  );

  const handleSendRequest = async (
    appointments,
    overrideRequiredActions = null
  ) => {
    setIsOpenSBEModal(false);

    // strip Kaiser GA transient fields (DOB/SSN) so they are not sent to the backend
    const sanitizedFormData = { ...formData };
    KAISER_GA_TRANSIENT_FIELDS.forEach(field => {
      delete sanitizedFormData[field];
    });
    SUNCARE_TRANSIENT_FIELDS.forEach(field => {
      delete sanitizedFormData[field];
    });

    const data = {
      ...sanitizedFormData,
      carrierName: carrier,
      states: appointments,
      transfer: formData.appointedType,
      carrierData,
      files: formData.files,
      requiredActions: overrideRequiredActions ?? requiredActions,
      expirationDates: formData.expirationDates,
      attachments
    };

    // the following is not an error
    dispatch(setRequestedCarrierName(carrier)); // optimisticly make the request to get appointments data
    const isSuccess = await createAppointmentRequest(
      cancelTokenSource,
      data,
      typeof carrierInProgress === 'function'
        ? carrierInProgress(carrierData.id, appointments)
        : () => {}
    ); // add requested appointments
    if (isSuccess) {
      setFormData(INITIAL_VALUES); // reset the form state
      setIsOpenRequiredActionsModal(false); // close the file upload modal
    } else {
      dispatch(setRequestedCarrierName(carrier)); // dispatch action to force reload and stop component from being stuck in loading state
    }
  };

  const handleSendContract = async () => {
    if (!formData.suncareContractOpened) return;

    const result = await dispatch(sendSuncareContractSent());

    if (result?.ok) {
      setFormData(INITIAL_VALUES);
      setRequiredActions({ filteredActions: [] });
      setIsOpenRequiredActionsModal(false);
    }
  };

  useEffect(() => {
    setFormData(INITIAL_VALUES);
    setRequiredActions({ filteredActions: [] });
  }, [carrier]);

  const tempOptions = arrayDifference(
    carrierConsidersStates ? statesOffered : [],
    Array.isArray(confirmed) ? confirmed : []
  );

  const options = getCarrierStateOptions(carrierData, user, tempOptions);

  const statesAvailableButNoLicenses =
    tempOptions.length > 0 && options.length === 0;

  const showAppointmentOptionsAndActions =
    !statesAvailableButNoLicenses &&
    !lowRankCarrierMessage &&
    !isSuncareAppointmentBlocked;

  // the shortcut is only worth showing while the agent still has something to submit below
  const hasActionableRequestSection =
    !simplified &&
    !isAgencyDisabled &&
    (showAppointmentOptionsAndActions || isFfmCertificationBlocked);

  const isSendRequestButtonInView = useIsElementInView(sendRequestButtonRef, [
    carrier,
    isNeo,
    hasActionableRequestSection
  ]);

  const showScrollToRequestButton =
    hasActionableRequestSection && !isSendRequestButtonInView;

  const requestSectionButtonText = isNeo
    ? 'Get Contracted'
    : 'Request Appointments';

  return (
    <>
      <div className='w-full min-w-0'>
        {carrierData.disclaimer?.placements?.has(
          DISCLAIMER_PLACEMENTS.NOT_APPOINTED_SIDEBAR
        ) && (
          <div className='mb-2.5 w-full min-w-0'>
            <CarrierDisclaimer disclaimer={carrierData.disclaimer} />
          </div>
        )}
        {!simplified && !isSuncareAppointmentBlocked && (
          <div className='mt-2.5 pt-2.5 border-t border-grey-lightest first:mt-0 first:border-t-0 first:pt-0'>
            <div className='text-center'>
              <p className='text-black text-base font-medium tracking-wide mb-3 mt-3 text-left m-0'>
                Not requested
              </p>
            </div>
          </div>
        )}
        {!simplified && isSuncarePendingDirector && (
          <div className='mt-2.5 pt-2.5 border-t border-grey-lightest first:mt-0 first:border-t-0 first:pt-0'>
            <p className='text-black text-base font-medium tracking-wide mb-3 mt-3 text-left m-0'>
              Pending
            </p>
            <div
              className={cn(
                'inline-flex rounded-2xl border-none px-2 py-0.5 text-base font-normal',
                '[&>span]:!bg-[var(--color-semantic-warning)] [&>span]:!text-[var(--color-warning-contrast)]'
              )}
            >
              <span>
                {getSuncareCardStatus(SUNCARE_PHASES.pendingDirector)}
              </span>
            </div>
          </div>
        )}
        {!simplified && isSuncareTrainingPending && (
          <div className='mt-2.5 pt-2.5 border-t border-grey-lightest first:mt-0 first:border-t-0 first:pt-0'>
            <p className='text-black text-base font-medium tracking-wide mb-3 mt-3 text-left m-0'>
              Status
            </p>
            <div
              className={cn(
                'inline-flex rounded-2xl border-none px-2 py-0.5 text-base font-normal',
                getSuncareTrainingBadgeClassName()
              )}
            >
              <span>
                {getSuncareCardStatus(SUNCARE_PHASES.uploadCertificate)}
              </span>
            </div>
          </div>
        )}

        {showScrollToRequestButton && (
          <ScrollToRequestButton
            text={requestSectionButtonText}
            onClick={scrollToRequestSection}
          />
        )}

        <CarrierFeatureNotes
          notes={carrierData.carrierFeatureDescriptionNotes}
        />

        {isNeo && (
          <NeoContractingSection
            sectionRef={requestSectionRef}
            sendButtonRef={sendRequestButtonRef}
            carrierData={carrierData}
            obFormCompleted={obFormCompleted}
            isAgencyDisabled={isAgencyDisabled}
            isRankRestricted={Boolean(lowRankCarrierMessage)}
            isCarrierDisabledDueToBlackout={isCarrierDisabledDueToBlackout}
            carrierDisabledDueToBlackoutMessage={
              carrierDisabledDueToBlackoutMessage
            }
          />
        )}

        {!isNeo && (
          <div
            ref={requestSectionRef}
            className='mt-2.5 pt-2.5 border-t border-grey-lightest first:mt-0 first:border-t-0 first:pt-0'
          >
            {isSuncarePendingDirector && <SuncareInProgressNotice />}
            {isSuncareTrainingPending && (
              <SuncareTrainingNotice userEmail={user?.Email} />
            )}

            {!simplified && !isSuncareAppointmentBlocked && (
              <p className='text-black text-base font-medium tracking-wide mb-3 mt-3 text-left m-0'>
                {isOneill ? 'Request New Appointments' : 'Request Appointments'}
              </p>
            )}

            {isAgencyDisabled && (
              <p className='text-left leading-tight text-semantic-danger m-0 mb-3'>
                {AGENCY_DISABLED_CARRIER_MESSAGE}
              </p>
            )}

            {!isAgencyDisabled &&
              showAppointmentOptionsAndActions &&
              isOneill && (
                <p className='text-left leading-tight'>
                  Choose a transfer type only if you&apos;re moving an existing
                  appointment to {companyNames.short}, then click Send Request
                </p>
              )}

            {!isAgencyDisabled &&
              showAppointmentOptionsAndActions &&
              carrierConsidersStates && (
                <StateSelector
                  formData={formData}
                  handleChangeAppointments={handleChangeAppointments}
                  options={options}
                />
              )}

            {!isAgencyDisabled && showAppointmentOptionsAndActions && (
              <TransferTypeSelector
                handleChangeAppointedType={handleChangeAppointedType}
                formData={formData}
                carrierName={carrier}
              />
            )}

            {!isAgencyDisabled && (
              <AppointmentMessages
                isInitialOnboardingFormIncomplete={!obFormCompleted}
                isFfmCertificationBlocked={isFfmCertificationBlocked}
                areApptsDisabled={false}
                hasOpenTransferRequests={false}
                statesAvailableButNoLicenses={statesAvailableButNoLicenses}
                isBlockedByBcbsCrossRequest={isBlockedByBcbsCrossRequest}
                bcbsCrossRequestBlockMessage={bcbsCrossRequestBlockMessage}
                isCarrierDisabledDueToBlackout={isCarrierDisabledDueToBlackout}
                carrierDisabledDueToBlackoutMessage={
                  carrierDisabledDueToBlackoutMessage
                }
              />
            )}

            {!simplified &&
              !isAgencyDisabled &&
              (showAppointmentOptionsAndActions ||
                isFfmCertificationBlocked) && (
                <SendRequestButton
                  ref={sendRequestButtonRef}
                  isDisabled={isDisabled}
                  isSendingRequestAppointments={isSendingRequestAppointments}
                  onSubmit={() => handleSubmit(formData.appointments)}
                />
              )}
          </div>
        )}
        {isOpenSBEModal && (
          <SBEModal
            requestedStates={formData.appointments}
            closeModal={() => setIsOpenSBEModal(false)}
            handleSubmit={handleSubmit}
            isLoading={isSendingRequestAppointments}
          />
        )}
      </div>

      {isOpenRequiredActionsModal && (
        <RequiredActionsModal
          handleClick={() => handleSendRequest(formData.appointments)}
          isLoading={isSendingRequestAppointments}
          isOpen={isOpenRequiredActionsModal}
          closeModal={() => setIsOpenRequiredActionsModal(false)}
          requiredActions={filteredActions}
          handleFilesChange={handleFilesChange}
          formData={formData}
          handleExpirationDatesChange={handleExpirationDatesChange}
          setAcknowledgement={handleAcknowledgement}
          userEmail={user?.Email}
          isSuncareContractStep={isSuncareContractStep}
          onSendContract={handleSendContract}
        />
      )}
    </>
  );
});

SideSectionUnnapointed.propTypes = {
  carrier: propTypes.string,
  confirmed: propTypes.array,
  statesOffered: propTypes.array,
  carrierConsidersStates: propTypes.bool,
  carrierData: propTypes.object,
  createAppointmentRequest: propTypes.func,
  setApptRequestStatus: propTypes.func,
  obFormCompleted: propTypes.bool,
  simplified: propTypes.bool,
  carrierInProgress: propTypes.func,
  isAgencyDisabled: propTypes.bool
};

export default SideSectionUnnapointed;
